import React from 'react';
import { useTranslation } from 'react-i18next';

import { UI_OPERATION_STATUS_REQUEST } from '@uncinc/react-drupal-core';

import { DOMAIN_MENTAAL_BALANS } from '../../../config/constants';
import useClasses from '../../../hooks/useClasses';
import useOperationStatus from '../../../hooks/useOperationStatus';
import usePopup from '../../../hooks/usePopup';
import useToast from '../../../hooks/useToast';
import useTypedDispatch from '../../../hooks/useTypedDispatch';
import { unpairClient } from '../../../store/actions/NursePractitioner';
import Avatar from '../../Avatar';
import Button from '../../Button';
import Popup from '../../Popup';

import './style.scss';

interface Props {
  uid: number;
  avatar: number;
  name: string;
  nickname?: string;
  domain: string;
  totalRegistrations: number;
  completedRegistrations?: number
  successes?: number
  fails?: number
  onUnpairSuccess: () => void
  hideRegistrations?: boolean
}

const BlockClientInfo: React.FC<Props> = ({
  uid,
  avatar,
  name,
  nickname,
  domain,
  totalRegistrations,
  completedRegistrations,
  successes,
  fails,
  onUnpairSuccess,
  hideRegistrations = false,
}) => {
  const { t } = useTranslation();
  const { buildClass } = useClasses('BlockClientInfo');
  const popup = usePopup('unpair-confirmation-popup', {
    closeOnEscape: true,
  });
  const dispatch = useTypedDispatch();
  const toast = useToast();

  const operationStatus = useOperationStatus('nursePractitioner', 'unpair_client');

  async function onUnpairClient() {
    await dispatch(unpairClient(domain, uid)).then(() => {
      popup.close();

      toast.show('Client is ontkoppeld', 'success');
      onUnpairSuccess();
    }).catch(() => {
      toast.show('Er is iets misgegaan', 'error');
    });
  }

  return (
    <>
      {popup.isOpen && (
        <Popup
          {...popup.popupProps}
          title={t('pages.client_details.unpair_confirmation.title')}
          closeButtonText={t('pages.client_details.unpair_confirmation.cancel')}
          cta={(
            <Button
              warning
              buttonState={operationStatus === UI_OPERATION_STATUS_REQUEST && 'disabled'}
              onClick={onUnpairClient}
            >
              {t('pages.client_details.unpair_confirmation.title')}
            </Button>
          )}
        >
          <p>{t('pages.client_details.unpair_confirmation.content')}</p>
        </Popup>
      )}
      <div className={buildClass()}>
        <div className={buildClass('basic-details')}>
          <Avatar avatar={avatar} className={buildClass('avatar')} />

          <div className={buildClass('name')}>
            <h1 className="asH4">{name}</h1>
          </div>

          {nickname && (
          <div className={buildClass('nickname')}>
            {nickname}
          </div>
          )}
        </div>

        <ul className={buildClass('program-info')}>
          <li key="program-info--program">
            {domain === DOMAIN_MENTAAL_BALANS ? (
              <strong>{t(`pages.client_details.program.title.${domain}`)}</strong>
            ) : (
              <strong>{t('pages.client_details.program.title.default')}</strong>
            )}
            <div>{t(`domains:short.${domain}`)}</div>
          </li>
          <li key="program-info--days-registered">
            <strong>{t('pages.client_details.week_progress.title')}</strong>
            <div>
              {t('pages.client_details.week_progress.body', {
                week: Math.max(1, Math.ceil(totalRegistrations / 7)),
              })}
            </div>
          </li>
        </ul>

        {!hideRegistrations && (
          <div className={buildClass('streaks')}>
            <div className={buildClass('streaks-content')}>
              <div className={buildClass('streaks-item')}>
                <i className="icon icon-streak" />
                <span>{t('common:blocks.streak.default.registration_days', { count: completedRegistrations, total: totalRegistrations })}</span>
              </div>
              <div className={buildClass('streaks-item')}>
                <i className="icon icon-happy" />
                <span>{t('common:blocks.streak.default.success', { count: successes })}</span>
              </div>
              <div className={buildClass('streaks-item')}>
                <i className="icon icon-sad" />
                <span>{t('common:blocks.streak.default.fails', { count: fails })}</span>
              </div>
            </div>
          </div>
        )}

        <Button className={buildClass('disconnect-client')} onClick={popup.open}>
          {t('pages.client_details.unpair')}
        </Button>
      </div>
    </>
  );
};

export default BlockClientInfo;
